import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import CheckboxFormField from "components/checkbox-form-field/index";
import ErrorSnackbarContent from "components/error-snackbar-content/index";
import InputFormField from "components/input-form-field/index";
import ModalForm from "components/modal-form/index";
import SelectFormField from "components/select-form-field/index";
import { EXTENDED_SUB_REFERRAL_SOURCE_FEATURE } from "constants/feature-flags";
import { REFERRAL_SOURCE_OBJECT_TYPES } from "constants/referral_sources";
import { useAddReferralSourceMutation } from "services/referral-sources";

interface SubReferralSourceShortFormProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd?: (id: number) => void;
  parentReferralSourceId: number;
}

interface SubReferralSourceFormValues {
  name: string;
  is_active: boolean;
  referred_type?: string;
  object_id?: number;
  object_type?: string;
}

const EMPTY_DEFAULT_VALUES: SubReferralSourceFormValues = {
  name: "",
  is_active: true,
  referred_type: "",
  object_id: undefined,
  object_type: undefined
};

export default function SubReferralSourceShortForm({
  isOpen,
  onClose,
  onAdd,
  parentReferralSourceId
}: SubReferralSourceShortFormProps) {
  const dispatch = useAppDispatch();
  const [createReferralSource] = useAddReferralSourceMutation();

  const onSubReferralSourceSave = useCallback(
    (data: SubReferralSourceFormValues) => {
      createReferralSource({
        name: data.name,
        is_active: data.is_active,
        parent: parentReferralSourceId,
        referred_type: data.referred_type,
        object_id: data.object_id,
        object_type: data.object_type
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          onAdd && onAdd(response.data.id);
          dispatch(setCustomMessage("Sub Referral Source added"));
          dispatch(setOpen(true));
          onClose();
        }
      });
    },
    [createReferralSource, dispatch, parentReferralSourceId, onAdd, onClose]
  );

  const flags = useFlags();
  const isExtendedSubReferralSourceEnabled =
    flags[EXTENDED_SUB_REFERRAL_SOURCE_FEATURE];

  const defaultValues: SubReferralSourceFormValues = useMemo(() => {
    return { ...EMPTY_DEFAULT_VALUES };
  }, []);

  const formMethods = useForm<SubReferralSourceFormValues>({
    mode: "onChange",
    defaultValues
  });
  const { control, handleSubmit, reset } = formMethods;

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      onSubReferralSourceSave(data);
    })();
  }, [handleSubmit, onSubReferralSourceSave]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <ModalForm
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={onSubmit}
      title="New Sub Referral Source"
      hideBackdrop={false}
      formName="sub-referral-source"
      maxWidth="md"
      control={control}
    >
      <Grid item container xs={12} spacing={4} alignItems="center">
        <Grid item xs={4}>
          <InputFormField
            name="name"
            label="Sub Referral Source"
            control={control}
            required
            sx={{}}
          />
        </Grid>
        {isExtendedSubReferralSourceEnabled && (
          <Grid item xs={4}>
            <SelectFormField
              name="referred_type"
              label="Associated Data"
              control={control}
              options={REFERRAL_SOURCE_OBJECT_TYPES.getOptions()}
            />
          </Grid>
        )}
        <Grid item xs={2} alignItems="center">
          <FormControlLabel
            label="Active"
            labelPlacement="start"
            control={
              <CheckboxFormField
                testId="is_active"
                name="is_active"
                control={control}
              />
            }
          />
        </Grid>
      </Grid>
    </ModalForm>
  );
}
